import React, { useEffect, useRef } from 'react';
import { trackGrowth } from '@/lib/growthTracking';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import SEOHead from '@/components/SEOHead';
import InstallAppButton from '@/components/InstallAppButton';
import { HeroVideo } from '@/components/HeroVideo';
import { organizationSchema, webSiteSchema, softwareApplicationSchema } from '@/lib/seoSchemas';
import {
  GitBranch, Calendar, BarChart3, FileDown, Users,
  ChevronRight, Zap, Shield, Star, ArrowRight, Check,
  LogIn, LogOut,
  Activity, Camera, Image as ImageIcon, Share2, Timer, Award, Download, Flag, Sparkles,
} from 'lucide-react';
import { OfficialsSpotlight } from '@/components/landing/OfficialsSpotlight';
import { ProblemSection, PillarsSection, FreeSchedulerBand } from '@/components/landing/PlatformSections';
import { SiteFooter } from '@/components/landing/SiteFooter';

export const TAGLINE = 'Your League. One Platform.';
const SEO_TITLE = 'Schedule Guru | All-in-One Sports League Management Software';
const SEO_DESCRIPTION = 'Manage your entire sports league with Schedule Guru. Generate schedules, manage teams, track standings and player statistics, organize playoffs, and assign officials—all in one platform.';
const OG_TITLE = 'Schedule Guru — Your League. One Platform.';
const OG_DESCRIPTION = 'From instant scheduling to complete league management, Schedule Guru gives sports organizers everything they need to run successful competitions.';

const FEATURES_BENTO = [
  {
    title: 'Live Stat Tracker',
    desc: 'Action-first tap flow with starting lineups, subs, swipe-to-undo, live game clock, and plus/minus — built for courtside.',
    img: '/screenshots/live-stats.jpg',
    span: 'lg:col-span-8 lg:row-span-2',
    icon: Activity,
  },
  {
    title: 'AI Box Score Scanning',
    desc: 'Snap a photo of any hand-written box score — AI auto-builds the full digital sheet in seconds.',
    img: '/screenshots/ai-scan.jpg',
    span: 'lg:col-span-4 lg:row-span-2',
    icon: Camera,
  },
  {
    title: 'Visual Bracket Builder',
    desc: 'Single & double elimination, consolation, and butterfly layouts with live SVG connector lines. Prints to a single landscape page.',
    img: '/screenshots/bracket-view.jpg',
    span: 'lg:col-span-4',
    icon: GitBranch,
  },
  {
    title: 'Smart Schedule Generator',
    desc: 'Auto-generates schedules with courts, venues, game days, blackout dates, and buffer times.',
    img: '/screenshots/schedule-view.jpg',
    span: 'lg:col-span-4',
    icon: Calendar,
  },
  {
    title: 'Shareable Recap Graphics',
    desc: 'One-tap shareable PNGs for box scores, career stat cards, and post-game recaps — federation-branded automatically.',
    img: '/screenshots/recap-share.jpg',
    span: 'lg:col-span-4',
    icon: Share2,
  },
  {
    title: 'Officials Scheduling',
    desc: 'Assign referees, umpires and table officials to your games with live availability, conflict warnings, one-tap email confirmations, and built-in officials pay tracking.',
    img: '/screenshots/officials-hub.jpg',
    span: 'lg:col-span-12 lg:row-span-2',
    icon: Flag,
    badge: 'New',
    href: '#officials',
  },
];

const EXTRA_FEATURES = [
  { icon: Award,     title: 'Public Career Stat Sheets', desc: 'Player report cards with leaderboard rankings, season highs & social share' },
  { icon: Timer,     title: 'Live Game Clock',           desc: 'Server-synced countdown, configurable quarter length, multi-device safe' },
  { icon: ImageIcon, title: 'Federation Branding',       desc: 'Upload your org logo once — every share carries your crest automatically' },
  { icon: Users,     title: 'Guest Stat-Keepers',        desc: 'Scorers join with a code — no login required for /join volunteers' },
  { icon: FileDown,  title: 'Export Everywhere',         desc: 'PDF, Excel, iCal, CSV box scores, and one-click WhatsApp / AirDrop / Mail share' },
  { icon: Shield,    title: 'Multi-Admin Collaboration', desc: 'Invite co-organizers as editors or viewers — works across iPad + phone in real time' },
  { icon: BarChart3, title: 'Auto-Sorted Standings',     desc: 'Live tiebreakers by PTS → Head-to-Head → Point Differential, computed on every score' },
  { icon: Star,      title: 'Multi-Admin Score Entry',   desc: 'Inline scoring with auto-advancing brackets and live public standings' },
];

const PLATFORM_PILLS = [
  'Scheduling', 'Teams & Rosters', 'Player Profiles', 'Standings',
  'Statistics', 'Playoffs', 'Officials'
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro, isAdmin, logout, loading } = useAuth();
  const viewTracked = useRef(false);

  useEffect(() => {
    if (loading || isAuthenticated || viewTracked.current) return;
    viewTracked.current = true;
    trackGrowth('landing_view');
  }, [loading, isAuthenticated]);

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 300);
  }, []);

  const getStarted = () => navigate(isAuthenticated ? '/create' : '/login?mode=register&redirect=/create');
  const exploreFeatures = () => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        ogTitle={OG_TITLE}
        ogDescription={OG_DESCRIPTION}
        path="/"
        type="website"
        jsonLd={[organizationSchema(), webSiteSchema(), softwareApplicationSchema()]}
      />

      {/* ========= NAV ========= */}
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img src="/logo-transparent.png" alt="Schedule Guru" className="h-16 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
              <a href="#features" className="hover:text-[#00247D] transition-colors">Features</a>
              <a href="#officials" className="hover:text-[#00247D] transition-colors inline-flex items-center gap-1.5" data-testid="nav-officials-link">
                Officials<span className="px-1.5 py-0.5 rounded bg-[#CF142B] text-white text-[10px] font-bold leading-none">NEW</span>
              </a>
              <Link to="/bracket-library" className="hover:text-[#00247D] transition-colors">Bracket Library</Link>
              <Link to="/pricing" className="hover:text-[#00247D] transition-colors">
                <Star className="w-4 h-4 inline mr-1" />Pricing
              </Link>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link to="/admin">
                      <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#00247D] hidden sm:flex">
                        <Shield className="w-4 h-4 mr-1" />Admin
                      </Button>
                    </Link>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate('/my-schedules')} className="text-slate-600 hover:text-[#00247D]" data-testid="nav-schedules-btn">
                    <Calendar className="w-4 h-4 mr-1" />My Schedules
                  </Button>
                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#00247D] text-white flex items-center justify-center text-xs">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <span className="text-sm font-medium text-slate-700 hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                    {isPro && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">PRO</span>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500" data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="border-[#00247D] text-[#00247D]" data-testid="nav-login-btn">
                    <LogIn className="w-4 h-4 mr-2" />Sign In
                  </Button>
                </Link>
              )}
              <Button onClick={() => navigate(isAuthenticated ? '/create' : '/login')} className="bg-[#00247D] hover:bg-[#001A5C] text-white btn-transition" data-testid="nav-create-btn">
                {isAuthenticated ? 'Create Event' : 'Sign Up Free'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* ========= HERO ========= */}
      <section className="bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* ---- LEFT: headline + CTAs ---- */}
            <div className="lg:col-span-7 animate-fade-in-up">
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white/10 border border-white/20 text-sm text-amber-300 font-medium">
                <Zap className="w-4 h-4" /> Free to start. No credit card required.
              </div>
              <a href="#officials" className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#CF142B]/90 hover:bg-[#CF142B] text-sm text-white font-semibold transition-colors" data-testid="hero-officials-pill">
                <Sparkles className="w-4 h-4" /> New: Officials Scheduling <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

            <p className="text-sm sm:text-base tracking-[0.25em] uppercase font-bold text-amber-300 mb-3" style={{ fontFamily: 'Barlow Condensed' }} data-testid="hero-tagline">
              {TAGLINE}
            </p>
            <h1 className="text-4xl md:text-6xl tracking-tight font-bold uppercase leading-[0.95] text-white mb-5"
              style={{ fontFamily: 'Barlow Condensed' }} data-testid="hero-headline">
              Everything You Need<br />
              to Run Your <span className="text-[#CF142B] font-extrabold">Sports League.</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-xl mb-6" data-testid="hero-supporting-text">
              Create schedules in seconds. Manage teams, rosters, player profiles, standings, statistics, playoffs, and officials—all from one powerful platform.
            </p>

            {/* Platform pillars */}
            <div className="flex flex-wrap gap-2 mb-9" data-testid="hero-platform-pills">
              {PLATFORM_PILLS.map(f => (
                <span key={f} className="px-3 py-1 rounded-md bg-white/10 border border-white/20 text-xs text-slate-300 font-medium">
                  {f}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button size="lg" onClick={getStarted}
                className="bg-white text-[#00247D] hover:bg-slate-100 btn-transition h-14 px-8 text-base font-bold rounded-md"
                data-testid="hero-create-btn">
                Get Started FREE <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={exploreFeatures}
                className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-14 px-8 text-base font-bold rounded-md"
                data-testid="hero-explore-btn">
                Explore All Features
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="hero-secondary-links">
              <Button variant="ghost" size="sm" onClick={() => navigate('/bracket-library')}
                className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="hero-library-btn">
                <GitBranch className="w-4 h-4 mr-1.5" />Browse Brackets
              </Button>
              <Button asChild variant="ghost" size="sm" className="text-amber-200 hover:text-white hover:bg-white/10" data-testid="track-stats-btn">
                <a href="https://scheduleguru.ws/join" aria-label="Track Stats — open the Schedule Guru live stat tracker">
                  <Download className="w-4 h-4 mr-1.5" /> Track Stats
                </a>
              </Button>
              <InstallAppButton variant="hero-compact" />
            </div>
            </div>

            {/* ---- RIGHT: hero video (stacks under on mobile) ---- */}
            <div className="lg:col-span-5 w-full animate-fade-in-up">
              <HeroVideo videoId="RBtYHEc-s1E" title="Schedule Guru — see it in action" />
            </div>
          </div>
        </div>
      </section>

      {/* ========= WHAT PROBLEM IT SOLVES / WHY / FREE SCHEDULER ========= */}
      <ProblemSection />
      <PillarsSection />
      <FreeSchedulerBand />

      {/* ========= FEATURES BENTO ========= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="features-detail">
        <div className="text-center mb-16">
          <span className="text-sm tracking-[0.2em] uppercase font-bold text-[#CF142B]" style={{ fontFamily: 'Barlow Condensed' }}>
            All Features
          </span>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold uppercase text-[#00247D] mt-3"
            style={{ fontFamily: 'Barlow Condensed' }}>
            Built for League & Tournament Organizers
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-lg">
            From a casual 4-team bracket to a 100-team league with pool play and playoffs, Schedule Guru handles it all.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 auto-rows-[280px] lg:auto-rows-[260px]">
          {FEATURES_BENTO.map((f, i) => (
            <div key={i}
              className={`${f.span} group relative rounded-2xl bg-slate-50 border border-slate-200 overflow-hidden hover:-translate-y-1 hover:border-[#00247D]/30 hover:shadow-card-hover transition-all duration-300`}
              data-testid={`feature-card-${i}`}>
              <div className="relative z-10 p-5 lg:p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#00247D]/10">
                    <f.icon className="w-5 h-5 text-[#00247D]" />
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-wide text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                    {f.title}
                  </h3>
                  {f.badge && <span className="px-2 py-0.5 rounded bg-[#CF142B] text-white text-[10px] font-bold uppercase tracking-wider" data-testid={`feature-badge-${i}`}>{f.badge}</span>}
                  {f.href && <a href={f.href} className="ml-auto text-xs font-bold text-[#00247D] hover:underline inline-flex items-center gap-1" data-testid={`feature-link-${i}`}>Learn more <ArrowRight className="w-3.5 h-3.5" /></a>}
                </div>
                <p className="text-sm text-slate-500 leading-relaxed max-w-md">{f.desc}</p>

                <div className="mt-auto pt-3 flex-1 min-h-0 relative">
                  <img src={f.img} alt={f.title}
                    className="w-full h-full object-cover object-top rounded-lg border border-slate-200 shadow-lg opacity-90 group-hover:opacity-100 transition-opacity"
                    loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/30 to-transparent rounded-lg pointer-events-none" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ========= EXTRA FEATURES ROW ========= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EXTRA_FEATURES.map((f, i) => (
            <Card key={i}
              className="border border-slate-200 hover:border-[#00247D]/30 hover:shadow-card-hover transition-all duration-300"
              data-testid={`extra-feature-${i}`}>
              <CardContent className="p-5">
                <f.icon className="w-6 h-6 text-[#00247D] mb-3" />
                <h4 className="text-sm font-bold uppercase tracking-wide text-slate-800 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  {f.title}
                </h4>
                <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ========= OFFICIALS SPOTLIGHT ========= */}
      <OfficialsSpotlight isAuthenticated={isAuthenticated} />

      {/* ========= CTA ========= */}
      <section className="bg-hero-gradient relative py-24">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold uppercase text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed' }}>
            Ready to Run Your League?
          </h2>
          <p className="text-lg text-slate-300 mb-4 max-w-xl mx-auto">
            Start with a free schedule, then add teams, stats, standings and officials as you grow. Free to start — upgrade when you need more.
          </p>
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {['Up to 100 Teams', 'Any Format', 'PDF & Excel Export', 'Officials Scheduling', 'Free Tier'].map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 rounded bg-white/10 text-sm text-white/80">
                <Check className="w-3.5 h-3.5 text-[#009E49]" /> {tag}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" onClick={getStarted}
              className="bg-white text-[#00247D] hover:bg-slate-100 h-14 px-10 text-base font-bold rounded-md btn-transition"
              data-testid="cta-create-btn">
              Get Started FREE <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/pricing')}
              className="border-white/30 text-white hover:bg-white/10 h-14 px-10 text-base font-bold rounded-md"
              data-testid="cta-pricing-btn">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
